import { isClosedStatus } from '@bbbk/schema';
import { isResidentNow, spansDay, dogCount, occupancyByDate } from '../bookings/domain';
import { isoDate } from '../../lib/dates';
import type { Booking, BookingDog, BookingStatus } from '../bookings/types';
import type { Shift, Staff } from '../staff/types';

// Pure data-shaping for the Today (daily dashboard) page. Each function takes the
// windowed bookings / shifts and the selected day (ISO YYYY-MM-DD) and returns
// the slice a band renders. Dates are compared as local ISO day-strings (via
// isoDate) so a full timestamp and a date-only string agree. Cancelled bookings
// never count.

// Drop-offs: bookings whose check-in is the day.
export function arrivingOn(bookings: Booking[], day: string): Booking[] {
  return bookings.filter((b) => !isClosedStatus(b.status) && isoDate(b.checkIn) === day);
}

// Collections: bookings whose check-out is the day.
export function collectingOn(bookings: Booking[], day: string): Booking[] {
  return bookings.filter((b) => !isClosedStatus(b.status) && isoDate(b.checkOut) === day);
}

// In residence (Freddi, 2026-06-17): two distinct calculations.
//  - LIVE today: who's physically in NOW = status 'arrived' (until collected).
//    Date is irrelevant — a dog leaving today still counts until it's collected.
//  - Any other day (projection for planning): date-based — every non-cancelled
//    booking spanning the day (check-in ≤ day < check-out), matching the
//    occupancy measure the calendar/roster use. ALL areas either way.
export function inResidenceOn(bookings: Booking[], day: string, live = false): Booking[] {
  if (live) return bookings.filter(isResidentNow); // the shared live-headcount rule
  return bookings.filter((b) => spansDay(b, day)); // the shared occupancy-span rule
}

// A dog plus the booking it rides on — the entry shape for the day chip lists
// (in-residence, arriving) that ResidentsFill renders. One home so the producers
// and the renderer can't drift.
export interface DayDog {
  dog: BookingDog;
  bookingId: string;
}

// The dogs in residence on the day — one entry per dog, carrying its booking id.
export function residentDogs(bookings: Booking[], day: string, live = false): DayDog[] {
  return inResidenceOn(bookings, day, live).flatMap((b) => b.dogs.map((dog) => ({ dog, bookingId: b.id })));
}

// The dogs arriving (checking in) on the day — one entry per dog with its
// booking id. Same shape as residentDogs so both feed ResidentsFill.
export function arrivingDogs(bookings: Booking[], day: string): DayDog[] {
  return arrivingOn(bookings, day).flatMap((b) => b.dogs.map((dog) => ({ dog, bookingId: b.id })));
}

// The headline "In residence" COUNT for the day — dogs, not stays.
//  - LIVE today: dogs of every arrived booking (the status rule via dogsOf), so
//    it equals the Bookings 'arrived' chip and the stat strip exactly — both
//    count all arrived dogs the same way, with NO date window. The caller must
//    pass the full arrived set (see useArrivedResidents), not the desk's ±2-day
//    window — an uncollected overstay sits outside that window yet is still in.
//  - Any other day: the occupancy projection for that day, so it equals the
//    calendar's "N in" and the roster's load. A single day's occupancy is
//    window-independent: any stay spanning the day is in the ±2 window anyway.
// residentDogs (the chip LIST) flattens real dog rows, so a dog-less arrived
// stay shows as +1 here (|| 1) without a chip — a data anomaly, not a drift.
export function residenceCount(bookings: Booking[], day: string, live = false): number {
  if (live) return dogCount(inResidenceOn(bookings, day, true));
  return occupancyByDate(bookings, [day]).get(day) ?? 0;
}

export interface DeskCounts {
  expected: number;
  done: number;
  toGo: number;
}

// Per-section progress: how many bookings, how many are at a "done" status
// (arrived/collected for drop-offs; collected for collections), how many remain.
export function deskCounts(bookings: Booking[], doneStatuses: BookingStatus[]): DeskCounts {
  const expected = bookings.length;
  const done = bookings.filter((b) => doneStatuses.includes(b.status)).length;
  return { expected, done, toGo: expected - done };
}

export interface DutyTeams {
  am: Staff[];
  pm: Staff[];
  onDuty: Staff[]; // deduped union of AM + PM
}

// What a shift wants before it stops being worth a warning. The week mirror
// marks any half-day below this "short"; it was written out as a bare `< 2` at
// the one place that reads it, which is how a business rule becomes invisible.
// It lives beside onDutyTeams so a second reader — the rota's own grid, if it
// ever grows the same marker — cannot pick a different number.
export const MIN_HELPERS_PER_SHIFT = 2;

// Who's on the desk for the day: AM = filled shifts in slots 0–3, PM = slots 4–7,
// each mapped to its staff member. onDuty is the deduped union (a helper working
// both halves appears once). Unfilled slots and other days are ignored.
export function onDutyTeams(shifts: Shift[], staff: Staff[], day: string): DutyTeams {
  const byId = new Map(staff.map((s) => [s.id, s]));
  const teamFor = (filter: (slot: number) => boolean): Staff[] => {
    const seen = new Set<string>();
    const out: Staff[] = [];
    for (const s of shifts) {
      if (s.date !== day || !s.staffId || !filter(s.slot)) continue;
      const person = byId.get(s.staffId);
      if (person && !seen.has(person.id)) {
        seen.add(person.id);
        out.push(person);
      }
    }
    return out;
  };
  const am = teamFor((slot) => slot < 4);
  const pm = teamFor((slot) => slot >= 4);
  const onDuty = [...new Map([...am, ...pm].map((s) => [s.id, s])).values()];
  return { am, pm, onDuty };
}
